import { Alert, Card, Grid, Group, SegmentedControl, Select, Stack, Text, Title } from "@mantine/core";
import { IconBasket, IconLock, IconMap2, IconTrees, IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { http } from "../../api/http";
import { Chart } from "../../components/Charts";
import { ChoroplethMap } from "../../components/ChoroplethMap";
import { DataTable } from "../../components/DataTable";
import { fmtNum, KpiGrid, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";

interface Agg { gazdinstava: number; vocnjaka: number; povrsina_ha: number; ubrano_t: number; prinos_t_ha: number | null; vrsta?: string; opstina?: string; opstina_code?: string }
interface Overview { sezona: number; potisnuto?: boolean; napomena?: string; gazdinstava?: number; vocnjaka?: number; povrsina_ha?: number;
  ubrano_t?: number; prinos_t_ha?: number | null; po_vrstama?: Agg[]; ostalo?: Record<string, unknown> | null; prag?: number }
type Geo = { features: { properties: { code: string; name: string; value: number | null; suppressed: boolean } }[] };

const seasons = (() => { const y = new Date().getFullYear(); return [y, y - 1, y - 2, y - 3].map(String); })();

/** Javna statistika voćarstva — samo agregati sa potiskivanjem malih ćelija (ZZLP BiH 12/25). Dostupno svima. */
export default function StatsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useT();
  const [sezona, setSezona] = useState(String(new Date().getFullYear()));
  const [opstina, setOpstina] = useState<string | null>(null);
  const [metric, setMetric] = useState("povrsina_ha");
  const ov = useQuery({ queryKey: ["pub-ov", sezona, opstina], queryFn: () => http.get<Overview>("/api/v1/public/stats/overview", { sezona, opstina }) });
  const byOp = useQuery({ queryKey: ["pub-by", sezona], queryFn: () => http.get<{ rows: Agg[]; ostalo: Record<string, unknown> | null; prag: number }>("/api/v1/public/stats/by", { dimension: "opstina", sezona }) });
  const map = useQuery({ queryKey: ["pub-map", metric, sezona], queryFn: () => http.get<Geo>("/api/v1/public/stats/map", { metric, sezona }) });
  const ts = useQuery({ queryKey: ["pub-ts", opstina], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/public/stats/timeseries", { opstina }) });
  const o = ov.data;
  const units = (map.data?.features ?? []).map((f) => ({ value: f.properties.code, label: t(f.properties.name) })).sort((a, b) => a.label.localeCompare(b.label));
  const unitName = units.find((u) => u.value === opstina)?.label;

  return (
    <Stack>
      <PageHeader title={embedded ? "Statistika voćarstva" : "Javna statistika voćarstva"}
        description="Zbirni podaci Registra voćnjaka: površine, broj voćnjaka i gazdinstava, berba i prinos po opštinama i vrstama voća. Objavljuju se samo agregati — bez imena, BPG-a i lokacija pojedinačnih voćnjaka."
        right={<>
          <Select w={200} clearable searchable placeholder={t("Sve opštine")} data={units} value={opstina} onChange={setOpstina} aria-label={t("Opština")} />
          <Select w={100} data={seasons} value={sezona} onChange={(v) => v && setSezona(v)} aria-label={t("Sezona")} />
        </>} />

      {o?.potisnuto ? (
        <Alert color="gray" icon={<IconLock />}>{t(o.napomena ?? "Podaci su zaštićeni")}</Alert>
      ) : (
        <KpiGrid cols={5} items={[
          { label: "Gazdinstava", value: fmtNum(o?.gazdinstava, 0), icon: <IconUsers /> },
          { label: "Voćnjaka", value: fmtNum(o?.vocnjaka, 0), icon: <IconTrees /> },
          { label: "Površina", value: fmtNum(o?.povrsina_ha, 1), unit: "ha", icon: <IconMap2 /> },
          { label: "Ubrano", value: fmtNum(o?.ubrano_t, 1), unit: "t", icon: <IconBasket />, color: "soil" },
          { label: "Prosječan prinos", value: fmtNum(o?.prinos_t_ha, 1), unit: "t/ha", icon: <IconBasket />, color: "soil" },
        ]} />
      )}

      <Grid>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Card withBorder>
            <Group justify="space-between" mb="xs">
              <Title order={5}>{t("Po opštinama")}</Title>
              <SegmentedControl size="xs" value={metric} onChange={setMetric} data={[
                { value: "povrsina_ha", label: t("Površina") }, { value: "vocnjaka", label: t("Voćnjaka") },
                { value: "ubrano_t", label: t("Berba") }, { value: "prinos_t_ha", label: t("Prinos") }]} />
            </Group>
            <ChoroplethMap data={map.data} height={380} selected={opstina} onSelect={(c) => setOpstina(c === opstina ? null : c)} />
            <Text size="xs" c="dimmed" mt={4}>{t("„—“ = podatak zaštićen (manje od")} {o?.prag ?? 3} {t("gazdinstva u ćeliji). Klik na opštinu filtrira prikaz.")}</Text>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Card withBorder h="100%">
            <Title order={5} mb="xs">{t("Po vrstama voća")}{unitName ? ` — ${unitName}` : ""}</Title>
            <Chart rows={(o?.po_vrstama ?? []).map((r) => ({ ...r, naziv: t(r.vrsta ?? "") }))} x="naziv" height={230}
              series={[{ key: "povrsina_ha", label: t("Površina (ha)") }]} />
            <DataTable rows={(o?.po_vrstama ?? []).map((r) => ({ ...r, vrsta: t(r.vrsta ?? "") })) as unknown as Record<string, unknown>[]} search={false} dense columns={[
              { key: "vrsta", label: "Vrsta" }, { key: "gazdinstava", label: "Gazdinstava", type: "integer" },
              { key: "povrsina_ha", label: "Površina (ha)", type: "number", decimals: 1 }, { key: "prinos_t_ha", label: "Prinos (t/ha)", type: "number", decimals: 1 },
            ]} />
            {o?.ostalo && <Text size="xs" c="dimmed" mt={4}>{t("Zaštićeno ćelija")}: {String(o.ostalo.potisnuto_celija)}{o.ostalo.povrsina_ha != null ? ` · ${t("ukupno")} ${fmtNum(o.ostalo.povrsina_ha, 1)} ha` : ""}</Text>}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Card withBorder>
            <Title order={5} mb="xs">{t("Berba kroz sezone")}{unitName ? ` — ${unitName}` : ""}</Title>
            <Chart rows={(ts.data ?? []).filter((r) => !r.potisnuto)} x="sezona" series={[{ key: "ubrano_t", label: t("Berba (t)"), color: "#1c7ed6" }]} height={240} />
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Card withBorder>
            <Title order={5} mb="xs">{t("Tabela po opštinama")}</Title>
            <DataTable rows={(byOp.data?.rows ?? []).map((r) => ({ ...r, opstina: t(r.opstina ?? "") })) as unknown as Record<string, unknown>[]} maxHeight={260} dense
              exportName={`statistika_opstine_${sezona}`} columns={[
                { key: "opstina", label: "Opština" }, { key: "gazdinstava", label: "Gazdinstava", type: "integer" },
                { key: "vocnjaka", label: "Voćnjaka", type: "integer" }, { key: "povrsina_ha", label: "Površina (ha)", type: "number", decimals: 1 },
                { key: "ubrano_t", label: "Berba (t)", type: "number", decimals: 1 }, { key: "prinos_t_ha", label: "Prinos (t/ha)", type: "number", decimals: 1 },
              ]} />
          </Card>
        </Grid.Col>
      </Grid>
      <Alert variant="light" color="gray" icon={<IconLock />}>
        {t("Zaštita ličnih podataka: objavljuju se samo zbirni podaci. Ćelije sa manje od")} {o?.prag ?? 3} {t("gazdinstva se ne prikazuju, a ako bi se skrivena vrijednost mogla izračunati iz zbira, skriva se i sljedeća najmanja ćelija. Vrijednosti su zaokružene. Pristup podacima sopstvenog gazdinstva dobija se nakon registracije i odobrenja zahtjeva.")}
      </Alert>
    </Stack>
  );
}
